import { t } from '../../i18n'
import type { EventOwnerType } from './eventOwnership'

interface EventFormTitleArgs {
  /** True when editing an existing event, false when adding a new one. */
  isEdit: boolean
  /**
   * The competition scope the form is running under — the already-resolved
   * `resolveEventFormScheduleId` answer, NOT the raw route/prop value. Present
   * means the event is owned by a competition (a season on Canlan); absent
   * means it is team-owned.
   */
  scheduleId?: string | null
}

/**
 * The add/edit event title.
 *
 * The title always names the owner: "Add Competition Event" for a
 * competition-scoped form, "Add Team Event" for a team-owned one. Both words
 * come from i18n rather than a literal — `competition` is tenant-switched
 * ("season" on Canlan, see `common/i18n/lang/canlan.en.json`), and `team`
 * goes through the same path so a future rename reaches it too.
 *
 * Returned unmodified in case; call sites pass it through `capitalizeWords`
 * exactly as they did the literal it replaces.
 */
export const buildEventFormTitle = ({
  isEdit,
  scheduleId,
}: EventFormTitleArgs): string => {
  const action = isEdit ? 'Edit' : 'Add'
  const owner = scheduleId ? t('common:competition', {}) : t('common:team', {})
  return `${action} ${owner} Event`
}

/**
 * One line under the title explaining WHERE the event will show up.
 *
 * The two kinds of event surface in different places, and the form otherwise
 * gives no hint which one is being created — the fields differ, but only if you
 * already know what to look for. Stated in terms of what the admin will see
 * afterwards rather than the data model.
 *
 * Sentence copy, so it is NOT Title-Cased at the call site.
 */
export const buildEventFormSubtitle = ({
  scheduleId,
}: Pick<EventFormTitleArgs, 'scheduleId'>): string => {
  const competition = t('common:competition', {})
  if (scheduleId) {
    return `This event will appear on the ${competition} schedule, and on the schedule of any team you assign it to.`
  }
  return 'This event will appear only on the team schedule.'
}

/**
 * Short label for the owner indicator badge on cards and the detail screen.
 *
 * Lives HERE rather than in `eventOwnership` deliberately: this module already
 * depends on i18n, whereas `eventOwnership` is pure resolution logic imported
 * by tests that do not mock i18n. Keeping the tenant copy on this side of the
 * line leaves the resolver dependency-free.
 *
 * Returned lowercase — each surface applies its own casing (the badges
 * uppercase it).
 */
export const getEventOwnerTypeLabel = (ownerType: EventOwnerType): string =>
  ownerType === 'competition'
    ? t('common:competition', {})
    : t('common:team', {})
